import Cookies from 'js-cookie';
import { createContext, useEffect, useState } from 'react';

import { SEGMENT_ANONYMOUS_ID_COOKIE_NAME } from '~/common/constants/user';

import type { ReactElement, ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

type Callback = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  next: (payload: any) => void;
  payload: {
    obj: {
      context?: {
        brand?: string;
      };
      properties: Record<string, unknown>;
      type: string;
    };
  };
};

// eslint-disable-next-line no-undef
interface ExtendedAnalytics extends SegmentAnalytics.AnalyticsJS {
  addDestinationMiddleware: (name: string, callback: ({ payload, next }: Callback) => void) => void;
}

export const AnalyticsContext = createContext({
  isInitialized: false,
  anonymousId: '',
});

export const AnalyticsProvider = ({ children }: Props): ReactElement => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [anonymousId, setAnonymous] = useState<string>(
    Cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE_NAME) || ''
  );

  useEffect(() => {
    if (!window.analytics || !window.analytics.load) {
      return;
    }

    window.analytics.load(process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY || '');

    window.analytics.ready(() => {
      setIsInitialized(true);
      setAnonymous(Cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE_NAME) || '');
    });
  }, []);

  useEffect(() => {
    if (isInitialized) {
      (window.analytics as ExtendedAnalytics).addDestinationMiddleware(
        'Google AdWords New',
        ({ payload, next }) => {
          if (['track', 'page'].includes(payload.obj.type)) {
            const traits = window.analytics.user().traits();
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const orderCount = Number(traits.order_count as string);
            const props = payload.obj.properties;
            const value = props.value ?? props.revenue ?? props.total ?? props.booking_value;
            const transactionId =
              props.transaction_id ??
              props.order_id ??
              props.orderId ??
              props.cart_id ??
              props.cartId ??
              props.id;
            let newCustomer;

            if (!isNaN(orderCount)) {
              newCustomer = orderCount > 0;
            }

            payload.obj.properties = {
              ...props,
              new_customer: newCustomer ?? null, // Explicit null instead of undefined
              brand: payload.obj.context?.brand || 'unknown',
              value: value ?? null, // Default to null if all falsy
              transaction_id: transactionId || `temp_${Date.now()}`, // Fallback ID
            };
          }

          next(payload);
        }
      );
    }
  }, [isInitialized]);

  return (
    <AnalyticsContext.Provider value={{ isInitialized, anonymousId }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
